import React, { createContext, useContext, useEffect, useState } from "react";
import { seededPostedJobs, computeRankIndex, currentUser } from "../mock";

const AppStoreContext = createContext(null);

const load = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};

export function AppStoreProvider({ children }) {
  const [requests, setRequests] = useState(() => load("gd_requests", {}));
  const [role, setRole] = useState(() => load("gd_role", "grow"));
  const [savedTotal, setSavedTotal] = useState(() => load("gd_saved", 175));

  // demo perspective: 'kid' | 'parent' | 'neighbor'
  const [viewAs, setViewAs] = useState(() => load("gd_viewas", "kid"));
  // neighbor-posted jobs shared across all roles
  const [postedJobs, setPostedJobs] = useState(() => load("gd_posted", seededPostedJobs));
  // interactive rank progress (added on top of base stats)
  const [bonusJobs, setBonusJobs] = useState(() => load("gd_bonusjobs", 0));
  const [bonusEarned, setBonusEarned] = useState(() => load("gd_bonusearned", 0));
  // programmable emergency contact for the SOS button
  const [emergency, setEmergency] = useState(() => load("gd_emergency", { name: "Mom (Sarah)", phone: "+1 (555) 012-3456" }));
  // safe-arrival check-ins per job: { [jobId]: { arrived: ISO|null, finished: ISO|null } }
  const [checkins, setCheckins] = useState(() => load("gd_checkins", {}));

  useEffect(() => localStorage.setItem("gd_requests", JSON.stringify(requests)), [requests]);
  useEffect(() => localStorage.setItem("gd_role", JSON.stringify(role)), [role]);
  useEffect(() => localStorage.setItem("gd_saved", JSON.stringify(savedTotal)), [savedTotal]);
  useEffect(() => localStorage.setItem("gd_viewas", JSON.stringify(viewAs)), [viewAs]);
  useEffect(() => localStorage.setItem("gd_posted", JSON.stringify(postedJobs)), [postedJobs]);
  useEffect(() => localStorage.setItem("gd_bonusjobs", JSON.stringify(bonusJobs)), [bonusJobs]);
  useEffect(() => localStorage.setItem("gd_bonusearned", JSON.stringify(bonusEarned)), [bonusEarned]);
  useEffect(() => localStorage.setItem("gd_emergency", JSON.stringify(emergency)), [emergency]);
  useEffect(() => localStorage.setItem("gd_checkins", JSON.stringify(checkins)), [checkins]);

  const markArrived = (jobId) =>
    setCheckins((c) => ({ ...c, [jobId]: { ...(c[jobId] || {}), arrived: new Date().toISOString() } }));
  const markFinished = (jobId) =>
    setCheckins((c) => ({ ...c, [jobId]: { ...(c[jobId] || {}), finished: new Date().toISOString() } }));
  const attachPhoto = (jobId, dataUrl) =>
    setCheckins((c) => ({ ...c, [jobId]: { ...(c[jobId] || {}), photo: dataUrl } }));
  const removePhoto = (jobId) =>
    setCheckins((c) => ({ ...c, [jobId]: { ...(c[jobId] || {}), photo: null } }));
  const resetCheckin = (jobId) =>
    setCheckins((c) => { const n = { ...c }; delete n[jobId]; return n; });

  // ----- existing sample-chore request flow -----
  const requestJob = (choreId, slot) =>
    setRequests((r) => ({ ...r, [choreId]: { slot, status: "pending", requestedAt: Date.now() } }));
  const approveJob = (choreId) =>
    setRequests((r) => (r[choreId] ? { ...r, [choreId]: { ...r[choreId], status: "approved" } } : r));
  const declineJob = (choreId) =>
    setRequests((r) => {
      const c = { ...r };
      delete c[choreId];
      return c;
    });
  const statusOf = (choreId) => requests[choreId]?.status || null;
  const addToSavings = (amt) => setSavedTotal((s) => s + amt);

  // ----- neighbor-posted jobs cross-role flow -----
  const postJob = (job) => {
    const id = "pj-" + Math.random().toString(36).slice(2, 8);
    setPostedJobs((j) => [{ ...job, id, status: "open" }, ...j]);
    return id;
  };
  const kidAcceptJob = (id) =>
    setPostedJobs((j) => j.map((x) => (x.id === id ? { ...x, status: "kid_accepted" } : x)));
  const parentApproveJob = (id) =>
    setPostedJobs((j) => j.map((x) => (x.id === id ? { ...x, status: "parent_approved" } : x)));
  const parentDeclineJob = (id) =>
    setPostedJobs((j) => j.map((x) => (x.id === id ? { ...x, status: "declined" } : x)));

  const pendingApprovalCount = postedJobs.filter((x) => x.status === "kid_accepted").length;

  // ----- interactive ranking -----
  const jobsDone = currentUser.tasksDone + bonusJobs;
  const earnedTotal = currentUser.earned + bonusEarned;
  const rankIndex = computeRankIndex(jobsDone, earnedTotal);
  const completeDemoJob = (pay = 25) => {
    setBonusJobs((b) => b + 1);
    setBonusEarned((e) => e + pay);
  };
  const resetRankProgress = () => {
    setBonusJobs(0);
    setBonusEarned(0);
  };

  return (
    <AppStoreContext.Provider
      value={{
        requests, requestJob, approveJob, declineJob, statusOf,
        role, setRole, savedTotal, addToSavings,
        viewAs, setViewAs,
        emergency, setEmergency,
        checkins, markArrived, markFinished, resetCheckin, attachPhoto, removePhoto,
        postedJobs, postJob, kidAcceptJob, parentApproveJob, parentDeclineJob, pendingApprovalCount,
        jobsDone, earnedTotal, rankIndex, completeDemoJob, resetRankProgress,
      }}
    >
      {children}
    </AppStoreContext.Provider>
  );
}

export const useAppStore = () => useContext(AppStoreContext);
